import { useState, useRef, KeyboardEvent } from 'react'
import { X, Plus } from 'lucide-react'

interface TagsInputProps {
  value: string[]
  onChange: (tags: string[]) => void
  placeholder?: string
  disabled?: boolean
  maxTags?: number
  suggestions?: string[]
}

/**
 * TagsInput Component
 *
 * A component for managing an array of tag strings.
 * - Type a tag and press Enter or comma to add
 * - Click X on a tag to remove it
 * - Optional suggestions dropdown for common tags
 */
export default function TagsInput({
  value,
  onChange,
  placeholder = 'Add a tag...',
  disabled = false,
  maxTags = 20,
  suggestions = [],
}: TagsInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const trimmedValue = inputValue.trim()

    // Add tag on Enter or comma
    if ((e.key === 'Enter' || e.key === ',') && trimmedValue) {
      e.preventDefault()
      addTag(trimmedValue)
    }

    // Remove last tag on Backspace if input is empty
    if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value.length - 1)
    }
  }

  const addTag = (tag: string) => {
    const normalizedTag = tag.toLowerCase().trim().replace(/,/g, '')
    if (!normalizedTag) return

    // Don't add duplicates
    if (value.includes(normalizedTag)) {
      setInputValue('')
      return
    }

    // Don't exceed max tags
    if (value.length >= maxTags) {
      return
    }

    onChange([...value, normalizedTag])
    setInputValue('')
    setShowSuggestions(false)
  }

  const removeTag = (index: number) => {
    if (disabled) return
    const newTags = [...value]
    newTags.splice(index, 1)
    onChange(newTags)
  }

  // Filter suggestions that haven't been added yet
  const filteredSuggestions = suggestions.filter(
    s => !value.includes(s.toLowerCase()) &&
         s.toLowerCase().includes(inputValue.toLowerCase())
  )

  return (
    <div className="relative">
      <div
        className={`flex flex-wrap gap-2 p-2 border rounded-lg min-h-[42px] ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500'
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag, index) => (
          <span
            key={index}
            className={`inline-flex items-center gap-1 px-2 py-1 text-sm rounded-md ${
              disabled
                ? 'bg-gray-200 text-gray-600'
                : 'bg-primary-100 text-primary-800'
            }`}
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  removeTag(index)
                }}
                className="text-primary-600 hover:text-primary-800 focus:outline-none"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}

        {!disabled && value.length < maxTags && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              setShowSuggestions(e.target.value.length > 0 && filteredSuggestions.length > 0)
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(inputValue.length > 0 && filteredSuggestions.length > 0)}
            onBlur={() => {
              // Delay to allow click on suggestion
              setTimeout(() => setShowSuggestions(false), 150)
            }}
            placeholder={value.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] border-none outline-none text-sm p-1 bg-transparent"
            disabled={disabled}
          />
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredSuggestions.slice(0, 10).map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => addTag(suggestion)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
            >
              <Plus className="h-3 w-3 text-gray-400" />
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {value.length >= maxTags && (
        <p className="text-xs text-gray-500 mt-1">
          Maximum of {maxTags} tags reached
        </p>
      )}
    </div>
  )
}

/**
 * Common GRC-related tag suggestions
 */
export const GRC_TAG_SUGGESTIONS = [
  'access-control',
  'authentication',
  'authorization',
  'backup',
  'change-management',
  'cloud',
  'compliance',
  'configuration',
  'data-protection',
  'encryption',
  'endpoint',
  'firewall',
  'governance',
  'incident-response',
  'logging',
  'monitoring',
  'network',
  'password',
  'patch-management',
  'physical-security',
  'privacy',
  'risk-assessment',
  'security-awareness',
  'third-party',
  'vulnerability',
]
